import type {AxiosError} from 'axios'
import {useEffect, JSX} from 'react'
import {Outlet} from 'react-router'
import {userApi} from '../../api/userApi'
import {ReactComponent as NotFound} from '../../assets/Icons/NotFound.svg'
import variables from '../../assets/styles/variables.module.scss'
import {BaseFooter} from '../../components/Footer/BaseFooter'
import {BaseHeader} from '../../components/Header/BaseHeader'
import {NoPermissionEmpower} from '../../components/NoPermission/NoPermissionEmpower'
import {NoPermissionWorkforce} from '../../components/NoPermission/NoPermissionWorkforce'
import {ShowToasterMessage} from '../../components/ShowToasterMessage/ShowToasterMessage'
import {LoadingPage} from '../../screens/LoadingPage'
import {
  disconnectSocket,
  initiateSocketConnection,
} from '../../socketio.service'
import {userActions} from '../../store/user/userActions'
import {userSelectors} from '../../store/user/userSelectors'
import {getAuthCredential, getIsAdminUI, getLoginUrl} from '../../utils'
import {applyPanelTheme, parsePanelTheme} from '../../utils/themeUtil'
import styles from './BaseLayout.module.scss'
import {useWt, useWickuiTranslate} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../Translation/English/global'

export const BaseLayout = (): JSX.Element => {
  const {setLocale} = useWickuiTranslate()
  const wt = useWt()

  const isAdminUI = getIsAdminUI()
  const token = getAuthCredential()

  const selectedLanguage = userSelectors.useSelectedLanguage()
  const productSwitcherWorkforcePermission =
    userSelectors.useProductSwitcherWorkforcePermission()
  const productSwitcherEmpowerPermission =
    userSelectors.useProductSwitcherEmpowerPermission()
  const workforces = userSelectors.useWorkforces()
  const isRedirectingToLogin = userSelectors.useIsRedirectingToLogin()

  // React Query — fetch session data
  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
  } = userApi.useCurrentUser(token)
  const {data: workforcesData, isLoading: workforcesLoading} =
    userApi.useWorkforces(token)
  const {data: productSwitcherData} = userApi.useProductSwitcher(
    !!token && isAdminUI,
  )

  // Zustand setters — sync query data into the store for cross-app consumers
  const setCurrentUser = userActions.useSetCurrentUser()
  const setWorkforces = userActions.useSetWorkforces()
  const setProductSwitcher = userActions.useSetProductSwitcher()
  const setPanelTheme = userActions.useSetPanelTheme()

  // Sync user data into Zustand
  useEffect(() => {
    if (userData) {
      setCurrentUser(userData)
      if (!isAdminUI) {
        const themeSource = userData.panelTheme
          ? parsePanelTheme(userData.panelTheme)
          : {
              header_bg_color: variables.mainBlueColor,
              theme_color: variables.mainBlueColor,
            }
        if (themeSource) applyPanelTheme(themeSource)
        setPanelTheme(themeSource || null)
      }
    }
  }, [userData, isAdminUI, setCurrentUser, setPanelTheme])

  // Sync workforces into Zustand — depends on userData so lastVisitedWorkforceID is available
  useEffect(() => {
    if (workforcesData?.response) {
      setWorkforces(workforcesData.response)
    }
  }, [workforcesData, userData, setWorkforces])

  // Sync product switcher into Zustand
  useEffect(() => {
    if (productSwitcherData) {
      setProductSwitcher(productSwitcherData)
      if (isAdminUI) {
        localStorage.setItem('user', JSON.stringify(userData))
      }
    }
  }, [productSwitcherData, isAdminUI, userData, setProductSwitcher])

  // Redirect to login when no token
  useEffect(() => {
    if (!token) {
      setTimeout(() => {
        const url = getLoginUrl()
        window.location.href = url
      }, 4000)
    }
  }, [token])

  useEffect(() => {
    setLocale(selectedLanguage)
  }, [setLocale, selectedLanguage])

  useEffect(() => {
    if (token) {
      initiateSocketConnection()
    }
    return (): void => {
      disconnectSocket()
    }
  }, [token])

  if (!token) {
    return <LoadingPage />
  }

  if (userError) {
    // A redirect to login is already in flight (restClient's 401 interceptor fires it
    // synchronously, before this error even reaches React Query) — window.location.href
    // doesn't stop this render, so show the same loading state as the !token case above
    // instead of a flash of the raw "Unauthorized" error body.
    if (isRedirectingToLogin) {
      return <LoadingPage />
    }
    const msg =
      (userError as AxiosError<{message: string}>)?.response?.data?.message ||
      wt(GLOBAL.somethingWentWrong)
    return (
      <>
        <BaseHeader />
        <div className={'error-message'}>{msg}</div>
        <BaseFooter />
      </>
    )
  }

  if (userLoading) {
    return <LoadingPage />
  }

  if (!productSwitcherWorkforcePermission) {
    return <NoPermissionWorkforce />
  }

  if (!productSwitcherEmpowerPermission) {
    return <NoPermissionEmpower />
  }

  if (!userLoading && !workforcesLoading && workforces.length < 1) {
    return (
      <>
        <BaseHeader />
        <main className="main-container">
          <div className={styles.noData}>
            <NotFound />
            <p className={styles.noDataTitle}>{wt(GLOBAL.noFolders)}</p>
            <p className={styles.noDataSubTitle}>{wt(GLOBAL.createFolders)}</p>
          </div>
          <BaseFooter />
        </main>
      </>
    )
  }

  return (
    <>
      <ShowToasterMessage />
      <>
        <BaseHeader />
        <main>
          <Outlet />
        </main>
        <BaseFooter />
      </>
    </>
  )
}
